import React, { useEffect, useRef, useState } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import i18n from "utils/i18n";
import Modal from 'theme/Modal';
import Spinner from "theme/Spinner";
import Text from "theme/Text";
import _ from "lodash";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout, { createRefreshControl } from "components/atoms/ThemedLayout";
import ThemedBlock from "components/atoms/ThemedBlock";
import { messageAttachmentConstants, messageConstants } from "utils/constants/message-constants";
import FetchPushNotification from "utils/fetch/FetchPushNotification";
import { StyleSheet, View } from "react-native";
import Button from "theme/Button";
import LoadingComponent from "components/atoms/Loading";
import { AppRoutes } from "navigations/app-routes";
import MutateMessage from "utils/mutate/message";
import MutatePushNotification from "utils/mutate/push-notification";
import { ThemedShared } from "styles/themed";
import HelperString from 'utils/helper-string'
import FetchMessage from "utils/fetch/FetchMessage";
import ThemedButtonContainer from 'components/atoms/ThemedButtonContainer'
import FetchCachedMessage from 'utils/fetch/cached/FetchCachedMessage'
import { constantsActive } from 'utils/constants/constants'
import NoResultsView from 'components/atoms/NoResultsView'
import HelperDate from 'utils/helper-date'
import { colorList } from "utils/colors";

const HomeMoreInboxViewScene = () => {
	
	const navigation = useNavigation();
	const route = useRoute();
	
	const [ loading, setLoading ] = useState( true )
	const [ refreshing, setRefreshing ] = useState( false )
	const [ archiving, setArchiving ] = useState( false )
	const [ message, setMessage ] = useState( null )
	const [ attachments, setAttachments ] = useState( [] );
	
	const messageGuid = useRef( _.get( route, ['params', 'guid'], null ) )
	const messageType = useRef( _.get( route, ['params', 'type'], null ) )
	
	const fetchMessage = () => {
		const guid = (messageGuid.current === null) ? message.guid : messageGuid.current
		
		switch( messageType.current ) {
			case messageConstants.MESSAGE:
				FetchMessage.fetchOne( guid, {
					callbackStart: setLoading.bind( this, true ),
					callbackSuccess: ( { message: newMessage } ) => {
						createAttachments( newMessage, () => {
							setLoading( false )
							setRefreshing( false )
							
							// Only set the message when it was empty or the new message is newer
							if( _.isEmpty( message ) || HelperDate.date( newMessage.updated_at ).isAfter( HelperDate.date( message.updated_at ) ) ) {
								setMessage( newMessage )
							}
						})
					},
					callbackError: () => {
						setLoading( false );
						setRefreshing( false )
					}
				});
				break;
			
			case messageConstants.PUSH_NOTIFICATION:
				FetchPushNotification.fetch( guid, {
					callbackStart: setLoading.bind( this, true ),
					callbackSuccess: ( { pushNotification, message } ) => {
						const guid = _.get( message, 'guid', null );
						if( !_.isEmpty( guid ) ) {
							messageType.current = messageConstants.MESSAGE
							messageGuid.current = guid;
							
							fetchMessage()
							return
						}
						
						setLoading( false )
						setRefreshing( false )
						setMessage( pushNotification )
					},
					callbackError: () => {
						setLoading( false )
						setRefreshing( false )
					}
				});
				break;
		}
	}
	
	const refreshMessage = () => {
		setRefreshing( true )
		fetchMessage()
	}
	
	const createAttachments = ( message, callback ) => {
		const buttons = [];
		
		const attachments = _.get( message, 'attachment', [] );
		if( _.size( attachments ) > 0 ) {
			_.forEach( attachments, attachment => {
				switch ( attachment.name ) {
					case messageAttachmentConstants.EMPLOYEE_BOOKING_INVITATION:
						buttons.push(
							<Button
								/* TODO optimize arrow */
								onPress={
									navigation.navigate.bind( this, AppRoutes.INVITATION_RECEIVED, {
										employee_booking_invitation_guid: attachment.guid,
									})
								}>
								
								{ i18n.t(`page.message_view.attachment.button_view_${attachment.name}`) }
							</Button>
						)
						break;
					
					case messageAttachmentConstants.EMPLOYEE_BOOKING_INVITATION_SENT:
						buttons.push(
							<Button
								/* TODO optimize arrow */
								onPress={
									navigation.navigate.bind( this, AppRoutes.INVITATION_SENT, {
										employee_booking_invitation_guid: attachment.guid,
									})
								}>
								
								{ i18n.t(`page.message_view.attachment.button_view_${attachment.name}`) }
							</Button>
						)
						break;
				}
			})
		}
		
		setAttachments( buttons )
		
		if( _.isFunction( callback ) ) {
			callback()
		}
	}
	
	const onArchiveMessage = () => {
		// Create the params
		const archiveParameters = {
			callbackStart: setArchiving.bind( this, true ),
			callbackError: setArchiving.bind( this,false ),
			callbackSuccess: ( {message} ) => {
				FetchCachedMessage.reload({
					callbackSuccess: () => {
						setArchiving( false )
						navigation.goBack();
					}
				})
			}
		}
		
		const guid = (messageGuid.current === null) ? message.guid : messageGuid.current
		
		switch( messageType.current ) {
			case messageConstants.MESSAGE:
				MutateMessage.archive( guid, archiveParameters )
				break;
			
			case messageConstants.PUSH_NOTIFICATION:
				MutatePushNotification.archive( guid, archiveParameters )
				break;
		}
	}
	
	useEffect( () => {
		return fetchMessage();
	}, [] )
	
	const subtitle = _.isEmpty( message ) ? null : HelperDate.date( message.created_at ).format('DD-MM-YYYY HH:mm' );
	
	// Attachments
	let attachmentsView = null;
	if( _.size( attachments ) > 0 ) {
		attachmentsView = (
			<ThemedBlock
				transparent>
				
				{ _.map( attachments, (button, buttonKey) => {
					return (
						<View
							key={ HelperString.hash('attachment', buttonKey )}>

							{ button }
						</View>
					);
				} ) }
			</ThemedBlock>
		)
	}
	
	let content = <LoadingComponent />
	if( !loading ) {
		content = (
			<NoResultsView>
				{ i18n.t(`management:generic.no_results` ) }
			</NoResultsView>
		)
		
		if( !_.isEmpty( message ) ) {
			content = [];
			
			if( !_.isEmpty( message.title ) || !_.isEmpty( message.body ) ) {
				content.push(
					<ThemedBlock
						mediumPadding
						shadow>
						
						{ !_.isEmpty( message.title ) && (
							<Text
								category={"label"}
								style={ staticStyles.textMarginBottom8FontSize16 }>
								
								{ message.title }
							</Text>
						)}
						
						<Text
							category={"p1"}>
							
							{ message.body }
						</Text>
					</ThemedBlock>
				)
			}
			
			content.push( attachmentsView )
			
			// Archive button
			if( message.active === constantsActive.YES ) {
				content.push(
					<ThemedButtonContainer>
						<Button
							status={ 'danger' }
							appearance={"ghost"}
							size={ 'small' }
							onPress={ onArchiveMessage.bind( this ) }>
							
							{ i18n.t('page.message_view.action.archive' ) }
						</Button>
					</ThemedButtonContainer>
				)
			}
		}
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				topNavigation={ true }
				showBackButton={ true }
				title={i18n.t( 'page.message_view.title')}
				subtitle={ subtitle }
				scrollViewProps={{
					...createRefreshControl({
						isRefreshing: refreshing,
						onRefresh: refreshMessage.bind( this )
					})
				}}>
				
				{ content }
			</ThemedLayout>
			
			{/* Is updating */}
			<Modal
				visible={ archiving }
				backdropStyle={ ThemedShared.backdrop }>
				
				<Spinner
					size='large'
					color={ colorList.RED.primary }
				/>
			</Modal>
		</ThemedSafeAreaComponentTop>
	);
}

const staticStyles = StyleSheet.create({
	textMarginBottom8FontSize16: {
		marginBottom: 8,
		fontSize: 16,
	},
});

export default HomeMoreInboxViewScene;
